import React, { useEffect, useState } from 'react';
import { obtenerReservasEvaluadas } from '../api/reservas';
import { extractHHmm, to12hWithAmPm } from '../utils/time';
import { useNotify } from '../components/NotificationProvider';
import { isAdminRole } from '../utils/helpers';

function toDDMMYYYY(value) {
  if (!value) return '—';
  const s = String(value);
  const ymd = s.includes('T') ? s.split('T')[0] : s;
  const [y, m, d] = (ymd || '').split('-');
  if (!y || !m || !d) return ymd || '—';
  return `${d}/${m}/${y}`;
}

const AdminEvaluaciones = ({ user, onLoginClick }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { error: notifyError, info: notifyInfo } = useNotify();

  useEffect(() => {
    const load = async () => {
      try {
        const data = await obtenerReservasEvaluadas(user);
        setItems(data);
      } catch (e) {
        notifyError('Error cargando evaluaciones: ' + e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [notifyError, user?.id, user?.role, user?.email, user?.telefono]);

  if (!user || !isAdminRole(user.role)) {
    return (
      <div className="text-center py-20 bg-white rounded-3xl shadow-xl">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Acceso Restringido</h2>
        <p className="text-gray-600 mb-6">Solo administradores.</p>
        <button onClick={onLoginClick} className="px-8 py-3 bg-purple-500 text-white font-bold rounded-xl shadow hover:bg-purple-600">Iniciar Sesión</button>
      </div>
    );
  }

  return (
    <div className="w-screen max-w-none -mx-[calc((100vw-100%)/2)] px-2 md:px-8 py-6">
      <h1 className="text-2xl font-bold mb-6 text-purple-700">Servicios Evaluados</h1>
      {loading && <p className="text-gray-500">Cargando...</p>}
      {!loading && items.length === 0 && (
        <div className="p-6 bg-white rounded-xl border border-purple-200 shadow text-center text-purple-500 font-semibold">No hay evaluaciones registradas.</div>
      )}
      {items.length > 0 && (
        <div className="w-full max-w-none rounded-xl border border-purple-200 shadow-lg bg-white overflow-visible">
          <table className="w-full table-fixed divide-y divide-purple-100 text-sm">
            <colgroup>
              <col style={{ width: '6rem' }} />
              <col style={{ width: '10rem' }} />
              <col style={{ width: '7rem' }} />
              <col style={{ width: '5.5rem' }} />
              <col style={{ width: '7rem' }} />
              <col style={{ width: '6rem' }} />
              <col style={{ width: '6rem' }} />
              <col style={{ width: '10rem' }} />
            </colgroup>
            <thead className="bg-purple-50">
              <tr>
                <th className="px-4 py-3 font-bold text-purple-700 text-left">N°</th>
                <th className="px-4 py-3 font-bold text-purple-700 text-left">Producto</th>
                <th className="px-4 py-3 font-bold text-purple-700 text-left">Fecha</th>
                <th className="px-4 py-3 font-bold text-purple-700 text-left">Hora</th>
                <th className="px-4 py-3 font-bold text-purple-700 text-left">Duración</th>
                <th className="px-4 py-3 font-bold text-purple-700 text-left">Rating</th>
                <th className="px-4 py-3 font-bold text-purple-700 text-left">Calificado</th>
                <th className="px-4 py-3 font-bold text-purple-700 text-left">Comentario</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-purple-100">
              {items.map(r => {
                const hhmm = extractHHmm(r.Hora);
                return (
                  <tr key={r.Id} className="align-top">
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-1 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold border border-indigo-200">{r.NumeroReserva || r.Id}</span>
                    </td>
                    <td className="px-4 py-3 break-words">
                      {r.NombreProducto || '—'}
                      {r.ProductoInexistente === 1 && (
                        <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">Eliminado</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{toDDMMYYYY(r.Fecha)}</td>
                    <td className="px-4 py-3">{hhmm ? to12hWithAmPm(hhmm) : '—'}</td>
                    <td className="px-4 py-3">{r.Duracion ? `${r.Duracion} min` : '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {Array.from({ length: r.Calificacion || 0 }).map((_, i) => (
                          <span key={i} className="text-yellow-400">★</span>
                        ))}
                        {Array.from({ length: 5 - (r.Calificacion || 0) }).map((_, i) => (
                          <span key={i} className="text-gray-300">★</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {r.CalificadoEn ? toDDMMYYYY(r.CalificadoEn) : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-pre-wrap">{r.ComentarioCalificacion || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminEvaluaciones;
